// Shared presentational modal for `kind == "prompt"` project variables.
//
// Pure UI: it takes the prompt-variable descriptors and an onSubmit callback;
// the per-surface caller owns the API wiring (bootstrap/save). Consumed by the
// designer frontend (In-app Run, Site preview, Designer) and the standalone
// runtime client via the `@twasta/page-runtime/userMemory` subpath.
//
// Theme: uses CSS custom properties with literal fallbacks so it looks right
// whether or not the host defines the Twasta theme variables.

import React, { useMemo, useState } from 'react'

export type PromptInputMode = 'input' | 'select'

// Sentinel option value used to switch a select-mode variable to free text.
const TYPE_SENTINEL = '__twasta_type_value__'

export interface PromptOption {
  value: string
  label: string
}

export interface PromptVariable {
  name: string
  prompt: string
  input_mode: PromptInputMode
  required: boolean
  external?: boolean
  remember_default: boolean
  default?: string
  description?: string
  value?: string | null
  remember?: boolean
  pending?: boolean
  options?: PromptOption[]
  /** True when a select variable's source/master table can't be read yet
   *  (e.g. not created on a fresh import). The UI then tells the user the
   *  master isn't defined and lets them type a value. */
  source_missing?: boolean
  /** The variable is the app's SECURITY CONTEXT ("Use in rights model"): the
   *  pick decides which entity-specific roles apply. Its options are already
   *  the values this user holds a role in; it never accepts free text, and
   *  whether it is remembered is the designer's call. */
  security_context?: boolean
}

/** Best-effort human message from a failed save (axios-shaped or Error). */
export function promptErrorMessage(err: unknown): string {
  const e = err as { response?: { data?: { detail?: unknown } }; message?: string }
  const detail = e?.response?.data?.detail
  if (typeof detail === 'string' && detail) return detail
  if (detail && typeof detail === 'object' && 'message' in (detail as object)) {
    return String((detail as { message: unknown }).message)
  }
  return e?.message || 'Could not save your choices.'
}

export interface PromptValue {
  var_name: string
  value: string
  remember: boolean
}

export interface UserPromptModalProps {
  variables: PromptVariable[]
  onSubmit: (values: PromptValue[]) => void | Promise<void>
  /** Called when the user dismisses without submitting (only possible when no
   *  required variable is unfilled, or when `dismissible` is forced true). */
  onClose?: () => void
  title?: string
  subtitle?: string
  /** Force-allow closing even with required vars unfilled (e.g. settings). */
  dismissible?: boolean
}

function initialValue(v: PromptVariable): string {
  if (v.value !== null && v.value !== undefined && v.value !== '') return String(v.value)
  if (v.default) return String(v.default)
  return ''
}

/**
 * Asks the logged-in user to supply each prompt-variable's value (type it, or
 * pick from a list), with a per-variable "Remember this" checkbox. Required
 * variables block submission until filled.
 */
export function UserPromptModal({
  variables, onSubmit, onClose, title, subtitle, dismissible,
}: UserPromptModalProps) {
  const [values, setValues] = useState<Record<string, string>>(() => {
    const o: Record<string, string> = {}
    for (const v of variables) o[v.name] = initialValue(v)
    return o
  })
  const [remember, setRemember] = useState<Record<string, boolean>>(() => {
    const o: Record<string, boolean> = {}
    for (const v of variables) o[v.name] = v.remember ?? v.remember_default ?? true
    return o
  })
  // Per-variable "type a free value" escape hatch for select-mode variables.
  // Defaults on when the list is empty (e.g. the source table isn't created
  // yet on a fresh import) or when the saved value isn't one of the options,
  // so the user is never deadlocked by an empty/constrained dropdown.
  // A security-context variable never offers it (the server refuses free text
  // there), and a list that loads with entries no longer needs it either —
  // the server only accepts a value from that list.
  const [typed, setTyped] = useState<Record<string, boolean>>(() => {
    const o: Record<string, boolean> = {}
    for (const v of variables) {
      if (v.input_mode !== 'select' || v.security_context) continue
      const opts = v.options || []
      o[v.name] = opts.length === 0 || !!v.source_missing
    }
    return o
  })
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const missingRequired = useMemo(
    () => variables.some((v) => v.required && !(values[v.name] || '').trim()),
    [variables, values],
  )
  const canDismiss = dismissible || !variables.some((v) => v.required)

  const submit = async () => {
    if (missingRequired || busy) return
    setBusy(true)
    setError(null)
    try {
      await onSubmit(variables.map((v) => ({
        var_name: v.name,
        value: values[v.name] ?? '',
        remember: v.security_context ? v.remember_default : !!remember[v.name],
      })))
    } catch (err) {
      // Keep the dialog open with the server's reason (e.g. "You have no
      // rights in 'FE2'") rather than closing on a pick that was not stored.
      setError(promptErrorMessage(err))
    } finally {
      setBusy(false)
    }
  }

  if (!variables.length) return null

  return (
    <div style={overlay} role="dialog" aria-modal="true">
      <div style={card}>
        <div style={{ padding: '18px 20px 6px' }}>
          <h2 style={titleStyle}>{title || 'Before you start'}</h2>
          <p style={subtitleStyle}>
            {subtitle || 'Please provide the following so the app can tailor your data.'}
          </p>
        </div>

        <div style={body}>
          {variables.map((v) => (
            <div key={v.name} style={field}>
              <label style={fieldLabel}>
                {v.prompt || v.name}
                {v.required && <span style={{ color: '#ef4444', marginLeft: 4 }}>*</span>}
              </label>
              {v.description ? <div style={hint}>{v.description}</div> : null}

              {v.security_context && (v.source_missing || (v.options || []).length === 0) ? (
                <div style={notice}>
                  {v.source_missing
                    ? 'The list of values cannot be read right now. Ask your administrator to check it.'
                    : 'You have no rights in any of these yet. Ask your administrator to assign you a role.'}
                </div>
              ) : null}

              {v.input_mode === 'select' && !v.security_context
                && (v.source_missing || (v.options || []).length === 0) ? (
                <div style={notice}>
                  This list isn’t available yet — its source data{' '}
                  {v.source_missing ? 'hasn’t been created' : 'has no entries'} for
                  this project. Type a value below, then add the same value to its
                  master/source data so it shows up in the list next time.
                </div>
              ) : null}

              {v.input_mode === 'select' && !typed[v.name] ? (
                <select
                  style={input}
                  value={values[v.name] ?? ''}
                  onChange={(e) => {
                    if (e.target.value === TYPE_SENTINEL) {
                      setTyped((s) => ({ ...s, [v.name]: true }))
                      return
                    }
                    setValues((s) => ({ ...s, [v.name]: e.target.value }))
                  }}
                >
                  <option value="">— Select —</option>
                  {(v.options || []).map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label || o.value}
                    </option>
                  ))}
                  {!v.security_context && ((v.options || []).length === 0 || v.source_missing) ? (
                    <option value={TYPE_SENTINEL}>✎ Type a value…</option>
                  ) : null}
                </select>
              ) : (
                <>
                  <input
                    style={input}
                    value={values[v.name] ?? ''}
                    onChange={(e) => setValues((s) => ({ ...s, [v.name]: e.target.value }))}
                    placeholder={v.default ? `Default: ${v.default}` : ''}
                  />
                  {v.input_mode === 'select' && (v.options || []).length > 0 ? (
                    <button
                      type="button"
                      style={linkBtn}
                      onClick={() => setTyped((s) => ({ ...s, [v.name]: false }))}
                    >
                      ↩ Choose from list
                    </button>
                  ) : null}
                </>
              )}

              {v.security_context ? (
                <div style={hint}>
                  {v.remember_default
                    ? 'Your choice is remembered for next time. You can switch at any time.'
                    : 'You will be asked again next time you sign in. You can switch at any time.'}
                </div>
              ) : (
                <label style={rememberRow}>
                  <input
                    type="checkbox"
                    checked={!!remember[v.name]}
                    onChange={(e) => setRemember((s) => ({ ...s, [v.name]: e.target.checked }))}
                  />
                  Remember this for next time
                </label>
              )}
            </div>
          ))}
          {error ? <div style={errorBox} role="alert">{error}</div> : null}
        </div>

        <div style={footer}>
          {canDismiss && onClose ? (
            <button type="button" style={btnSecondary} onClick={onClose} disabled={busy}>
              Skip for now
            </button>
          ) : <span />}
          <button
            type="button"
            style={{ ...btnPrimary, opacity: missingRequired || busy ? 0.5 : 1 }}
            onClick={submit}
            disabled={missingRequired || busy}
          >
            {busy ? 'Saving…' : 'Continue'}
          </button>
        </div>
      </div>
    </div>
  )
}

export default UserPromptModal

// ── Styles ────────────────────────────────────────────────────────────────
const overlay: React.CSSProperties = {
  position: 'fixed', inset: 0, zIndex: 10000,
  background: 'rgba(0,0,0,0.5)',
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  padding: 16,
}
const card: React.CSSProperties = {
  width: 'min(520px, 96vw)', maxHeight: '90vh', overflow: 'hidden',
  display: 'flex', flexDirection: 'column',
  background: 'var(--color-bg, #fff)', color: 'var(--color-text, #111827)',
  borderRadius: 12, boxShadow: '0 20px 60px rgba(0,0,0,0.35)',
}
const titleStyle: React.CSSProperties = { margin: 0, fontSize: '1.1625rem', fontWeight: 700 }
const subtitleStyle: React.CSSProperties = {
  margin: '6px 0 0', fontSize: '0.9125rem',
  color: 'var(--color-text-secondary, #6b7280)',
}
const body: React.CSSProperties = {
  padding: '12px 20px', overflowY: 'auto',
  display: 'flex', flexDirection: 'column', gap: 14,
}
const field: React.CSSProperties = { display: 'flex', flexDirection: 'column', gap: 5 }
const fieldLabel: React.CSSProperties = { fontSize: '0.9125rem', fontWeight: 600 }
const hint: React.CSSProperties = {
  fontSize: '0.8125rem', color: 'var(--color-text-secondary, #6b7280)',
}
const input: React.CSSProperties = {
  padding: '8px 10px', borderRadius: 6,
  border: '1px solid var(--color-border, #d1d5db)',
  background: 'var(--lp-input-bg, #fff)', color: 'var(--color-text, #111827)',
  fontSize: '0.9625rem', outline: 'none', width: '100%', boxSizing: 'border-box',
}
const notice: React.CSSProperties = {
  fontSize: '0.8125rem', lineHeight: 1.45,
  padding: '7px 9px', borderRadius: 6,
  background: 'rgba(245, 158, 11, 0.12)',
  border: '1px solid rgba(245, 158, 11, 0.45)',
  color: 'var(--color-text, #92400e)',
}
const errorBox: React.CSSProperties = {
  fontSize: '0.8625rem', lineHeight: 1.45, padding: '8px 10px', borderRadius: 6,
  background: 'rgba(239, 68, 68, 0.1)', border: '1px solid rgba(239, 68, 68, 0.45)',
  color: 'var(--color-error, #b91c1c)',
}
const linkBtn: React.CSSProperties = {
  alignSelf: 'flex-start', padding: 0, marginTop: 2,
  background: 'none', border: 'none', cursor: 'pointer',
  color: 'var(--color-primary, #2563eb)', fontSize: '0.8125rem',
}
const rememberRow: React.CSSProperties = {
  display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.8625rem',
  color: 'var(--color-text-secondary, #6b7280)', cursor: 'pointer',
}
const footer: React.CSSProperties = {
  padding: '12px 20px 18px', display: 'flex', justifyContent: 'space-between',
  alignItems: 'center', gap: 8,
  borderTop: '1px solid var(--color-border, #e5e7eb)',
}
const btnPrimary: React.CSSProperties = {
  padding: '8px 18px', borderRadius: 6, border: 'none', cursor: 'pointer',
  background: 'var(--color-primary, #2563eb)', color: '#fff',
  fontSize: '0.9625rem', fontWeight: 600,
}
const btnSecondary: React.CSSProperties = {
  padding: '8px 14px', borderRadius: 6, cursor: 'pointer',
  border: '1px solid var(--color-border, #d1d5db)', background: 'transparent',
  color: 'var(--color-text, #111827)', fontSize: '0.9625rem',
}
